import { Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };

import type { ChatThreadMeta } from '@/spaces/chat/useChatThreads.ts';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

const formatChatDate = (dateStr: string): string => {
	const chatDate = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - chatDate.getTime();
	const diffMinutes = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffMinutes < 1) return 'Just now';
	if (diffMinutes === 1) return '1 minute ago';
	if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
	if (diffHours === 1) return '1 hour ago';
	if (diffHours < 24) return `${diffHours} hours ago`;
	if (diffDays === 1) return 'Yesterday';

	return chatDate.toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'short',
		day: 'numeric',
		...(chatDate.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
	});
};

const ThreadItem = ({
	thread,
	isSelected,
	onSelect,
}: {
	thread: ChatThreadMeta;
	isSelected: boolean;
	onSelect: () => void;
}) => {
	return (
		<button
			onClick={onSelect}
			data-testid="thread-item"
			data-chat-id={thread.id}
			className={cn(
				`flex w-full flex-col gap-1 rounded-lg px-3 py-2 text-left
				transition-colors`,
				'hover:bg-(--glass-bg-hover)',
				isSelected && 'bg-(--glass-bg-hover)',
			)}
		>
			<div className="flex items-center gap-2">
				<span
					data-testid="thread-unread-dot"
					data-unread={thread.is_unread ? 'true' : 'false'}
					className={cn(
						'h-2 w-2 shrink-0 rounded-full',
						thread.is_unread ? 'bg-beacon-unread' : 'invisible',
					)}
				/>
				<Text
					styles={style({
						font: 'body',
						color: 'body',
						fontSize: 'body-sm',
						fontWeight: 'bold',
					})}
				>
					{thread.title || 'New chat'}
				</Text>
			</div>
			<Text
				styles={style({
					font: 'body',
					color: 'gray-600',
					fontSize: 'body-xs',
					marginStart: 16,
				})}
			>
				{formatChatDate(thread.updated_at)}
			</Text>
		</button>
	);
};

interface ChatHistoryPanelProps {
	currentChatId: string | null;
	onSelectChat: (chatId: string) => void;
	isClosing: boolean;
	threads: ChatThreadMeta[];
}

export const ChatHistoryPanel = ({
	currentChatId,
	onSelectChat,
	isClosing,
	threads,
}: ChatHistoryPanelProps) => {
	return (
		<div
			data-testid="chat-history-panel"
			className={cn(
				`glass-panel flex h-full w-[280px] shrink-0 flex-col overflow-hidden
				rounded-r-none border-r border-(--glass-border)`,
				isClosing
					? 'animate-thread-list-slide-out'
					: 'animate-thread-list-slide-in',
			)}
		>
			<div className="flex h-[57px] items-center px-4 py-3" />
			<div className="scrollbar-hidden flex-1 overflow-y-auto px-1 pt-3 pb-3">
				{threads.length === 0 ? (
					<div className="flex items-center justify-center py-8">
						<span className="text-sm text-(--text-muted)">
							No chat history yet
						</span>
					</div>
				) : (
					<div className="flex flex-col gap-1">
						{threads.map((thread) => (
							<ThreadItem
								key={thread.id}
								thread={thread}
								isSelected={thread.id === currentChatId}
								onSelect={() => {
									trackEvent('Chat Switched', { chat_id: thread.id });
									onSelectChat(thread.id);
								}}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
